import React from "react";
import { Instagram, Youtube, Twitter, Facebook, Mail, ArrowUpRight } from "lucide-react";
import { usePrice } from "@/lib/price";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";
const COMMUNITY = "https://whop.com/jobhacki/";
const AFFILIATE_SIGNUP = "https://whop.com/jobhacki/affiliates";

const buildCols = (price) => [
  {
    title: "Tools",
    links: [
      { label: "Playbooks", href: "/playbooks" },
      { label: "Jobs", href: "/jobs" },
      { label: "Toolbox", href: "/toolbox" },
      { label: "Resume Builder", href: "/resume" },
      { label: "Simulator", href: "/simulator" },
      { label: "OmniCut", href: "/omnicut" },
      { label: "OmniScript", href: "/omniscript", tag: "SOON" },
    ],
  },
  {
    title: "Account",
    links: [
      { label: "Dashboard", href: "/dashboard" },
      { label: "Log in", href: "/login" },
      { label: "Affiliate", href: "/affiliate", tag: "EARN" },
      { label: "Community", href: COMMUNITY, external: true },
      { label: `Unlock ${price}`, href: WHOP, external: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Disclaimer", href: "/disclaimer" },
      { label: "Refund policy", href: "/refund" },
      { label: "Contact", href: "mailto:support@jobhacki.com" },
    ],
  },
];

const socials = [
  { Icon: Instagram, href: "https://instagram.com/jobhacki", label: "Instagram @jobhacki" },
  { Icon: Twitter, href: "https://x.com/jobhacki", label: "X @jobhacki" },
  { Icon: Youtube, href: "https://youtube.com/@jobhacki", label: "YouTube @jobhacki" },
  { Icon: Facebook, href: "https://facebook.com/jobhacki", label: "Facebook @jobhacki" },
];

export default function Footer() {
  const { price } = usePrice();
  const cols = buildCols(price);
  return (
    <footer data-testid="footer" className="bg-[#0a0a0a] text-[#FDFBF7]">
      <div className="border-t-8 border-[#00FF66]" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <a
              href="/"
              data-testid="footer-logo"
              className="flex items-center gap-2 mb-4"
            >
              <img
                src="/jobhacki-logo.png"
                alt="JobHacki"
                className="h-10 w-10 border-2 border-[#FFE600] rounded-md object-cover"
              />
              <span className="font-display text-2xl">JOBHACKI</span>
            </a>
            <p className="font-mono-body text-sm mt-4 text-[#FDFBF7]/70 max-w-xs">
              The built AI + income systems, ready to use. Grounded, no hype.
              A growing community of members and counting.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {socials.map((s, i) => {
                const Icon = s.Icon;
                return (
                  <a
                    key={s.href}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${i}`}
                    aria-label={s.label}
                    title={s.label}
                    className="w-10 h-10 border-2 border-[#FFE600] flex items-center justify-center text-[#FFE600] hover:bg-[#FFE600] hover:text-[#0a0a0a] transition-colors"
                  >
                    <Icon size={18} strokeWidth={2.5} />
                  </a>
                );
              })}
            </div>
            <a
              href="mailto:support@jobhacki.com"
              data-testid="footer-email"
              className="mt-5 inline-flex items-center gap-2 font-mono-body text-xs text-[#FDFBF7]/60 hover:text-[#FFE600]"
            >
              <Mail size={14} strokeWidth={2.5} />
              support@jobhacki.com
            </a>
          </div>
          {cols.map((g) => (
            <div key={g.title}>
              <div className="font-accent text-xs text-[#FFE600] mb-4">
                {g.title.toUpperCase()}
              </div>
              <ul className="space-y-2">
                {g.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      {...(l.external && {
                        target: "_blank",
                        rel: "noopener noreferrer",
                      })}
                      className="inline-flex items-center gap-1.5 font-mono-body text-sm text-[#FDFBF7]/80 hover:text-[#FFE600] hover:underline underline-offset-4"
                    >
                      {l.label}
                      {l.tag && (
                        <span className="font-accent text-[9px] bg-[#FFE600] text-[#0a0a0a] px-1.5 py-0.5">
                          {l.tag}
                        </span>
                      )}
                      {l.external && <ArrowUpRight size={12} strokeWidth={3} />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t-2 border-[#FFE600]/30 pt-6">
          <div className="font-mono-body text-xs text-[#FDFBF7]/60">
            © {new Date().getFullYear()} JobHacki — Built loud. Built fast.
          </div>
          <div className="font-mono-body text-xs text-[#FDFBF7]/60">
            Grounded, no hype. Made for the unreasonable.
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <div className="font-display text-[18vw] leading-[0.85] text-center text-[#FFE600] -mb-4 sm:-mb-6 select-none">
          JOBHACKI
        </div>
      </div>
    </footer>
  );
}
